import {
  GeckoColorScaleThreshold,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

export interface ParsedColorThreshold {
  min: number
  mid: number
  max: number
  unit: string
}

export interface ParsedMeasurementRange {
  min: number
  max: number
  unit: string
}

/**
 * Parse color thresholds from metadata JSON
 */
export function parseColorThresholdsFromMetadata(
  metadata: Record<string, unknown> | undefined
): Record<string, ParsedColorThreshold> | null {
  if (!metadata?.colorThresholds) return null

  try {
    return JSON.parse(metadata.colorThresholds as string) as Record<
      string,
      ParsedColorThreshold
    >
  } catch (error) {
    console.error('Failed to parse color thresholds from metadata:', error)
    return null
  }
}

/**
 * Parse filter ranges from metadata JSON
 */
export function parseFilterRangesFromMetadata(
  metadata: Record<string, unknown> | undefined
): Record<string, ParsedMeasurementRange> | null {
  if (!metadata?.filterRanges) return null

  try {
    return JSON.parse(metadata.filterRanges as string) as Record<
      string,
      ParsedMeasurementRange
    >
  } catch (error) {
    console.error('Failed to parse filter ranges from metadata:', error)
    return null
  }
}

/**
 * Parse selected CDF IDs from metadata JSON
 */
export function parseSelectedCdfIdsFromMetadata(
  metadata: Record<string, unknown> | undefined
): Map<string, string> {
  if (!metadata?.selectedCdfIds) {
    return new Map<string, string>()
  }

  try {
    const parsed = JSON.parse(metadata.selectedCdfIds as string)
    const cdfIdMap = new Map<string, string>()
    Object.entries(parsed).forEach(([componentId, cdfId]) => {
      cdfIdMap.set(componentId, cdfId as string)
    })
    return cdfIdMap
  } catch (error) {
    console.error('Failed to parse selected CDF IDs from metadata:', error)
    return new Map<string, string>()
  }
}

/**
 * Serialize color thresholds to metadata format
 */
export function serializeColorThresholds(
  colorThresholds: Map<string, GeckoColorScaleThreshold>
): string {
  const thresholdsObj: Record<string, ParsedColorThreshold> = {}
  colorThresholds.forEach((threshold, key) => {
    thresholdsObj[key] = {
      min: threshold.min.num,
      mid: threshold.mid.num,
      max: threshold.max.num,
      unit: threshold.min.unit.id,
    }
  })
  return JSON.stringify(thresholdsObj)
}

/**
 * Serialize filter ranges to metadata format
 */
export function serializeFilterRanges(
  filteredRanges: Map<string, GeckoMeasurementRange>
): string {
  const filterRangesObj: Record<string, ParsedMeasurementRange> = {}
  filteredRanges.forEach((range, key) => {
    filterRangesObj[key] = {
      min: range.min.num,
      max: range.max.num,
      unit: range.min.unit.id,
    }
  })
  return JSON.stringify(filterRangesObj)
}

/**
 * Create a color threshold from parsed metadata
 */
export function createColorThresholdFromParsed(
  parsed: ParsedColorThreshold
): GeckoColorScaleThreshold {
  return GeckoColorScaleThreshold.CreateFromValues(
    parsed.min,
    parsed.mid,
    parsed.max,
    GeckoMeasurementUnit.GetByID(parsed.unit) || GeckoMeasurementUnit.None
  )
}

/**
 * Create a measurement range from parsed metadata
 */
export function createMeasurementRangeFromParsed(
  parsed: ParsedMeasurementRange
): GeckoMeasurementRange {
  return GeckoMeasurementRange.CreateFromValues(
    parsed.min,
    parsed.max,
    GeckoMeasurementUnit.GetByID(parsed.unit) || GeckoMeasurementUnit.None
  )
}
